/**
 * ПОЛНЫЙ ПУТЬ: D:\QR\QR-WEB\dashboard\src\components\QR\ScanQR.js
 * ВЕРСИЯ: 1. 1. 0 - БЕЗ AppLayout  
 * ВРЕМЯ СОЗДАНИЯ: 14.11.2025 22:00  
 */

import React, { useState, useRef, useEffect } from 'react';
import jsQR from 'jsqr';
import './ScanQR.css';

const API_URL = 'https://qr-money.onrender.com/api';

export const ScanQR = () => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const scanningRef = useRef(false);
  
  const [scanning, setScanning] = useState(false);
  const [qrInput, setQrInput] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState(null);
  const [scanMode, setScanMode] = useState('manual');

  useEffect(() => {
    const loadUser = async () => {
      try {
        const userId = localStorage.getItem('userId');
        const authToken = localStorage.getItem('authToken');
        const response = await fetch(`${API_URL}/profile/${userId}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
          },
        });
        if (response.ok) {
          const data = await response.json();
          if (data.success) setUser(data.data);
        }
      } catch (err) {
        console.warn('⚠️ Ошибка загрузки:', err);
      }
    };
    loadUser();
  }, []);

  const decodeQRFromCanvas = (canvas) => {
    try {
      const ctx = canvas.getContext('2d');
      if (!ctx) return null;
      
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const code = jsQR(imageData.data, imageData.width, imageData.height);
      
      if (code) {
        console.log('✅ QR найден:', code.data);
        return code.data;
      }
      return null;
    } catch (err) {
      console.error('❌ Ошибка декодирования:', err);
      return null;
    }
  };

  const startScanning = async () => {
    try {
      setScanning(true);
      setScanMode('camera');
      setError('');
      setSuccess('');
      scanningRef.current = true;

      console.log('📷 Запрос доступа к камере...');

      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'environment',
          width: { ideal: 1280 },
          height: { ideal: 720 }
        },
      });

      console.log('✅ Камера получена:', stream);

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        
        videoRef.current.onloadedmetadata = () => {
          console.log('📹 Видео загружено, размер:', {
            width: videoRef.current.videoWidth,
            height: videoRef.current.videoHeight
          });
          videoRef.current.play()
            .then(() => {
              console.log('▶️ Видео воспроизводится');
              scanQRFromVideo();
            })
            .catch(err => {
              console.error('❌ Ошибка воспроизведения:', err);
              setError('❌ Ошибка воспроизведения видео');
            });
        };

        videoRef.current.onerror = (err) => {
          console.error('❌ Ошибка видео:', err);
          setError('❌ Ошибка видео потока');
        };
      }
    } catch (err) {
      console.error('❌ Ошибка камеры:', err);
      setError(`❌ ${err.message || 'Нет доступа к камере'}`);
      setScanning(false);
      scanningRef.current = false;
    }
  };

  const scanQRFromVideo = () => {
    if (!videoRef.current || !canvasRef.current) {
      console.warn('⚠️ Refs не готовы');
      return;
    }

    const canvas = canvasRef.current;
    const video = videoRef.current;
    const ctx = canvas.getContext('2d');
    
    if (!ctx) {
      console.error('❌ Canvas context не получен');
      return;
    }

    let frameCount = 0;
    let lastLogTime = Date.now();

    const scanLoop = () => {
      if (!scanningRef.current) {
        console.log('🛑 Сканирование остановлено');
        return;
      }

      try {
        if (video.readyState === video.HAVE_ENOUGH_DATA) {
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;
          
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          
          frameCount++;
          const now = Date.now();
          if (now - lastLogTime > 2000) {
            console.log(`📊 Обработано кадров: ${frameCount}`);
            lastLogTime = now;
            frameCount = 0;
          }
          
          const qrCode = decodeQRFromCanvas(canvas);
          if (qrCode) {
            const id = extractQRId(qrCode);
            if (id) {
              console.log('🎉 QR код распознан:', id);
              setQrInput(id);
              setSuccess('✅ QR распознан с камеры!');
              scanningRef.current = false;
              stopScanning();
              return;
            }
          }
        } else {
          console.log('⏳ Видео загружается... readyState:', video.readyState);
        }
      } catch (err) {
        console.error('❌ Ошибка в цикле:', err);
      }

      requestAnimationFrame(scanLoop);
    };

    scanLoop();
  };

  const extractQRId = (data) => {
    try {
      if (data.includes('id=')) {
        const match = data.match(/id=([a-f0-9\-]+)/i);
        return match ? match[1] : data;
      }
      return data;
    } catch {
      return data;
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setScanMode('upload');
    setError('');
    setSuccess('');

    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        try {
          const tempCanvas = document.createElement('canvas');
          const ctx = tempCanvas.getContext('2d');
          
          if (!ctx) {
            setError('❌ Ошибка обработки');
            return;
          }

          tempCanvas.width = img.width;
          tempCanvas.height = img.height;
          ctx.drawImage(img, 0, 0);

          const qrCode = decodeQRFromCanvas(tempCanvas);
          if (qrCode) {
            const id = extractQRId(qrCode);
            setQrInput(id);
            setSuccess('✅ QR распознан из картинки!');
          } else {
            setError('❌ QR не найден в изображении');
          }
        } catch (err) {
          console.error('❌ Ошибка:', err);
          setError('❌ Ошибка обработки изображения');
        }
      };
      img.src = event.target?.result;
    };
    reader.readAsDataURL(file);
  };

  const stopScanning = () => {
    console.log('🛑 Остановка сканирования...');
    scanningRef.current = false;
    
    if (videoRef.current?.srcObject) {
      const tracks = videoRef.current.srcObject.getTracks();
      console.log('📽️ Останавливаем треки:', tracks.length);
      tracks.forEach((track) => {
        track.stop();
        console.log('✅ Трек остановлен:', track.kind);
      });
    }
    
    setScanning(false);
  };

  const handleScanQR = async (e) => {
    e.preventDefault();
    
    if (!qrInput.trim()) {
      setError('❌ Введи ID QR!');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const userId = localStorage.getItem('userId');
      const authToken = localStorage.getItem('authToken');
      
      if (!userId) {
        throw new Error('Пользователь не найден');
      }

      console.log('📤 Отправляю:', { qr_code_id: qrInput.trim(), to_user_id: userId });

      const response = await fetch(`${API_URL}/qr/scan`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          qrCodeId: qrInput.trim(),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Ошибка сканирования');
      }

      const data = await response.json();

      if (data.success) {
        setSuccess(`✅ Успешно!\n💰 +${data.transaction?.amount} ${data.transaction?.currency || 'DAR'}`);
        setQrInput('');
        stopScanning();
      } else {
        throw new Error(data.error || 'Ошибка');
      }
    } catch (err) {
      console.error('❌ Ошибка:', err);
      setError(`❌ ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="scan-qr-page">
      <div className="scan-qr-container">
        <div className="scan-qr-header">
          <h1>📱 Получить Деньги</h1>
          <p>Сканирование, загрузка или ввод QR</p>
        </div>

        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}

        {scanning && scanMode === 'camera' && (
          <div className="video-container">
            <video 
              ref={videoRef} 
              className="video-stream"
              playsInline
              autoPlay
            />
            <canvas ref={canvasRef} style={{ display: 'none' }} />
            <div className="scan-overlay">
              <div className="scan-frame"></div>
            </div>
            <button onClick={stopScanning} className="stop-btn" type="button">
              ⏹️ Стоп
            </button>
          </div>
        )}

        <form onSubmit={handleScanQR} className="scan-qr-form">
          <div className="form-group">
            <label htmlFor="qr-input">📊 ID QR</label>
            <input
              id="qr-input"
              type="text"
              value={qrInput}
              onChange={(e) => setQrInput(e.target.value)}
              placeholder="Введи ID или отсканируй"
              disabled={loading || scanning}
              autoComplete="off"
            />
          </div>

          <div className="scan-mode-buttons">
            <button
              type="button"
              onClick={startScanning}
              className="mode-btn camera-btn"
              disabled={loading || scanning}
            >
              📷 Камера
            </button>

            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="mode-btn upload-btn"
              disabled={loading || scanning}
            >
              🖼️ Загрузить
            </button>

            <button
              type="submit"
              className="mode-btn submit-btn"
              disabled={loading || scanning}
            >
              {loading ? '⏳ Подождите' : '✅ Готово'}
            </button>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleImageUpload}
            style={{ display: 'none' }}
          />
        </form>

        <div className="how-it-works">
          <h3>ℹ️ 3 способа:</h3>
          <div className="steps">
            <div className="step">
              <div className="step-number">📷</div>
              <div className="step-text">Камера</div>
            </div>
            <div className="step">
              <div className="step-number">🖼️</div>
              <div className="step-text">Загрузи</div>
            </div>
            <div className="step">
              <div className="step-number">⌨️</div>
              <div className="step-text">Вручную</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScanQR;
